/* useAdminActivityStream — iter100.247.
 *
 * Single source of truth for the Admin activity feed used by two
 * surfaces on the Overview page:
 *   • AdminTicker.jsx — the Bloomberg-style marquee at the top.
 *   • Overview.jsx    — the "Recent activity" section at the bottom.
 *
 * Backend: GET /api/admin/activity/stream?token=X (Server-Sent Events).
 *   - Emits an initial `update` event on connect.
 *   - Re-checks every 15s; emits fresh `update` only when the feed
 *     signature changes.
 *   - Heartbeat colon-comments between updates keep the pipe open.
 *
 * Returns:
 *   { items, connected, error }
 *     items      — null while loading; [] on empty; array of events otherwise.
 *     connected  — boolean; true after SSE handshake, false on any error.
 *     error      — Error object if connection could not be established.
 *
 * Design notes:
 *   • Two components mounted at the same time will open two SSE pipes
 *     to the same backend — the server handles this fine and the extra
 *     bandwidth is negligible (payload ≤ ~4 KB per push, one push per
 *     15s only when data changes). Keeping the hook self-contained is
 *     worth the trade-off over a module-level ref-counted cache.
 *   • EventSource auto-reconnects with browser-controlled backoff on
 *     network blips — no custom retry logic needed.
 */
import { useEffect, useRef, useState } from "react";

export default function useAdminActivityStream(limit = 15) {
    const [items, setItems] = useState(null);
    const [connected, setConnected] = useState(false);
    const [error, setError] = useState(null);
    const esRef = useRef(null);

    useEffect(() => {
        const token = localStorage.getItem("ca_token");
        if (!token) {
            setError(new Error("No auth token"));
            return;
        }
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/activity/stream?token=${encodeURIComponent(token)}&limit=${limit}`;
        const es = new EventSource(url);
        esRef.current = es;
        es.onopen = () => {
            setConnected(true);
            setError(null);
        };
        es.addEventListener("update", (ev) => {
            try {
                const feed = JSON.parse(ev.data);
                setItems(Array.isArray(feed) ? feed : []);
            } catch {
                // ignore malformed payloads — the next tick will retry
            }
        });
        es.onerror = () => {
            setConnected(false);
        };
        return () => {
            es.close();
            esRef.current = null;
        };
    }, [limit]);

    return { items, connected, error };
}
